import { lazy } from 'react';
import DashboardLayout from "@/components/layout/DashboardLayout";
import { AuthProvider } from "./contexts/AuthContext";
import { Navigate, Outlet } from "react-router-dom";
import { IntegrationScope, RedirectEditStep, RedirectStartStep } from "@/integrationRoutes";
import IntegrationStudioPage from '@/pages/integrationStudioPage';
import PrivacyPolicy from '@/pages/privacyPolicy';
import TermsService from '@/pages/termsService';
import HelpCenter from '@/pages/helpCenter';
const SettingsPage = lazy(() => import('@/pages/settings'));
import { AccountRedirect, SystemSettingsRedirect, CacheGridRedirect, BillingRedirect } from '@/pages/settings/redirects';
import ConfirmAccount from '@/pages/confirmAccount';
import MockDemoPage from '@/pages/mockdemo';
import OAuthError from '@/pages/oauthError';
import ErrorPage from '@/pages/errorPage';
import ErrorBoundary from '@/components/ErrorBoundary';

// Root layout that provides AuthProvider
const RootLayout = () => (
  <AuthProvider>
    <Outlet />
  </AuthProvider>
);

// Lazy load components
const SignIn = lazy(() => import('@/pages/signin'));
const MFAVerify = lazy(() => import('@/pages/mfa-verify'));
const SignUp = lazy(() => import('@/pages/signup'));
const SignupAdditionalDetails = lazy(() => import('@/pages/signupAdditionalDetails'));
const ProductSelection = lazy(() => import('@/pages/productSelection'));
const PaymentDetails = lazy(() => import('@/pages/paymentDetails'));
const SignupSuccess = lazy(() => import('@/pages/signupSuccess'));
const OAuthSuccess = lazy(() => import('@/pages/oauthSuccess'));
const ForgotPassword = lazy(() => import('@/pages/forgotPassword'));
const ResetPassword = lazy(() => import('@/pages/resetPassword'));
const Dashboard = lazy(() => import('@/pages/dashboard'));
const PartnerHome = lazy(() => import('@/pages/partner'));
const DetailsPage = lazy(() => import('@/pages/detailsPage'));
const PublicConnectorGenerate = lazy(() => import('@/pages/publicConnectorGenerate'));
const PublicSourceConfiguration = lazy(() => import('@/pages/publicSourceConfiguration'));
const PublicDestinationConfiguration = lazy(() => import('@/pages/publicDestinationConfiguration'));
const PublicDataMapping = lazy(() => import('@/pages/publicDataMapping'));
const PublicIntegrationProfile = lazy(() => import('@/pages/publicIntegrationProfile'));
const PublicConfigsCards = lazy(() => import('@/pages/publicConfigsCards'));
const EditConfigurePublicConnector = lazy(() => import('@/pages/editConfigurePublicConnector'));
const EditPublicConnector = lazy(() => import('@/pages/editPublicConnector'));
const IntegrationSetup = lazy(() => import('@/pages/integrationSetup'));
const IntegrationEngine = lazy(() => import('@/pages/integrationEngine'));
const IntelligenceInsights = lazy(() => import('@/pages/IntelligenceInsights'));
const CaptureDataMappingPage = lazy(() => import('@/pages/captureDataMapping'));
const DataDimensionStudio = lazy(() => import('@/pages/DataDimensionStudio'));
const WorkspaceLenses = lazy(() => import('@/pages/WorkspaceLenses'));
const CamerasLibrary = lazy(() => import('@/pages/Cameras'));
const CameraTitlePage = lazy(() => import('@/pages/Cameras/CameraTitle'));
const WatchPage = lazy(() => import('@/pages/Cameras/Watch'));
const FleetMapPage = lazy(() => import('@/pages/Fleet/FleetMapPage'));
const FleetVehiclesPage = lazy(() => import('@/pages/Fleet/VehiclesPage'));
const FleetDriversPage = lazy(() => import('@/pages/Fleet/DriversPage'));
const FleetPlacesPage = lazy(() => import('@/pages/Fleet/PlacesPage'));
const EngineDiagnosisPage = lazy(() => import('@/pages/Fleet/EngineDiagnosisPage'));
const AutomationStudio = lazy(() => import('@/pages/AutomationStudio'));
const ConnectorLibrary = lazy(() => import('@/pages/connectorLibrary'));
const ConnectorPage = lazy(() => import('@/pages/connectorPage'));
const StartIntegration = lazy(() => import('@/pages/startIntegration'));
const SyncSchedule = lazy(() => import('@/pages/syncSchedule'));
const SourceConfiguration = lazy(() => import('@/pages/sourceConfiguration'));
const DestinationConfiguration = lazy(() => import('@/pages/destinationConfiguration'));
const EnrichmentConfiguration = lazy(() => import('@/pages/enrichmentConfiguration'));
const DataMapping = lazy(() => import('@/pages/dataMapping'));
const MasterEntityConfiguration = lazy(() => import('@/pages/masterEntityConfiguration'));
const IntegrationReview = lazy(() => import('@/pages/integrationReview'));
const IntegrationProfile = lazy(() => import('@/pages/integrationProfile'));
const FAQ = lazy(() => import('@/pages/faq'));
const MasterCacheGridPage = lazy(() => import('@/pages/systemSettings/MasterCacheGridPage'));

export const routes = [
  // Public routes that don't require authentication
  {
    path: "/terms-service",
    element: <TermsService />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/privacy-policy",
    element: <PrivacyPolicy />,
    errorElement: <ErrorPage />,
  },
  {
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/sign-in",
        element: <SignIn />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/mfa-verify",
        element: <MFAVerify />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/confirm-account",
        element: <ConfirmAccount />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/sign-up",
        element: <SignUp />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/sign-up/additional-details",
        element: <SignupAdditionalDetails />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/sign-up/product-selection",
        element: <ProductSelection />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/sign-up/payment",
        element: <PaymentDetails />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/sign-up/success",
        element: <SignupSuccess />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/oauth-success",
        element: <OAuthSuccess />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/forgot-password",
        element: <ForgotPassword />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/reset-password",
        element: <ResetPassword />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/oauth-success",
        element: <OAuthSuccess />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/oauth-error",
        element: <OAuthError />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/",
        element: <DashboardLayout />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "",
            element: <Dashboard />,
          },
          {
            path: "dashboard",
            element: <Dashboard />,
          },
          {
            path: "partner",
            element: <PartnerHome />,
          },
          {
            path: "details/:id",
            element: <DetailsPage />,
          },
          {
            path: "details/:id/studio",
            element: <IntegrationStudioPage />,
          },
          {
            path: "mockdemo",
            element: <MockDemoPage />,
          },
          {
            path: "generate-public-connector/:fieldType",
            element: <PublicConnectorGenerate />,
          },
          {
            path: "public/source-configuration",
            element: <PublicSourceConfiguration />,
          },
          {
            path: "public/destination-configuration",
            element: <PublicDestinationConfiguration />,
          },
          {
            path: "public/data-mapping",
            element: <PublicDataMapping />,
          },
          {
            path: "public/integration-profile",
            element: <PublicIntegrationProfile />,
          },
          {
            path: "public/public-integration",
            element: <PublicConfigsCards />,
          },
          {
            path: "configure-public-connector/:fieldType/:id",
            element: <EditConfigurePublicConnector />,
          },
          {
            path: "edit-public-connector-config/:fieldType/:id",
            element: <EditConfigurePublicConnector />,
          },
          {
            path: "edit-public-connector/:id",
            element: <EditPublicConnector />,
          },
          {
            path: "integration-setup",
            element: <IntegrationSetup />,
          },
          {
            path: "integration-engine",
            element: <IntegrationEngine />,
          },
          {
            path: "intelligence-insights",
            element: (
              <ErrorBoundary>
                <IntelligenceInsights />
              </ErrorBoundary>
            ),
          },
          {
            path: "data-mapping",
            element: (
              <ErrorBoundary>
                <CaptureDataMappingPage />
              </ErrorBoundary>
            ),
          },
          {
            path: "data-dimension-studio",
            element: (
              <ErrorBoundary>
                <DataDimensionStudio />
              </ErrorBoundary>
            ),
          },
          {
            path: "workspace-lenses",
            element: (
              <ErrorBoundary>
                <WorkspaceLenses />
              </ErrorBoundary>
            ),
          },
          {
            path: "cameras",
            element: (
              <ErrorBoundary>
                <CamerasLibrary />
              </ErrorBoundary>
            ),
          },
          {
            path: "cameras/:cameraId",
            element: (
              <ErrorBoundary>
                <CameraTitlePage />
              </ErrorBoundary>
            ),
          },
          {
            path: "cameras/:cameraId/watch/:eventId",
            element: (
              <ErrorBoundary>
                <WatchPage />
              </ErrorBoundary>
            ),
          },
          {
            path: "fleet",
            element: (
              <ErrorBoundary>
                <FleetMapPage />
              </ErrorBoundary>
            ),
          },
          {
            path: "fleet/vehicles",
            element: (
              <ErrorBoundary>
                <FleetVehiclesPage />
              </ErrorBoundary>
            ),
          },
          {
            path: "fleet/drivers",
            element: (
              <ErrorBoundary>
                <FleetDriversPage />
              </ErrorBoundary>
            ),
          },
          {
            path: "fleet/places",
            element: (
              <ErrorBoundary>
                <FleetPlacesPage />
              </ErrorBoundary>
            ),
          },
          {
            path: "fleet/engine",
            element: (
              <ErrorBoundary>
                <EngineDiagnosisPage />
              </ErrorBoundary>
            ),
          },
          {
            path: "automation-studio",
            element: <AutomationStudio />,
          },
          {
            path: "connector-library",
            element: <ConnectorLibrary />,
          },
          {
            path: "connector-library/:id",
            element: <ConnectorPage />,
          },
          {
            path: "integration/:id",
            element: <IntegrationScope />,
            children: [
              { index: true, element: <StartIntegration /> },
              { path: "source-configuration", element: <SourceConfiguration /> },
              { path: "destination-configuration", element: <DestinationConfiguration /> },
              {
                path: "enrichment-configuration/:enrichmentTitle/:enrichmentId",
                element: <EnrichmentConfiguration />,
              },
              { path: "data-mapping", element: <DataMapping /> },
              { path: "sync-schedule", element: <SyncSchedule /> },
              { path: "master-entity", element: <MasterEntityConfiguration /> },
              { path: "integration-profile", element: <IntegrationProfile /> },
              { path: "review", element: <IntegrationReview /> },
            ],
          },
          { path: "start-integration", element: <Navigate to="/connector-library" replace /> },
          { path: "start-integration/integration-profile", element: <Navigate to="/connector-library" replace /> },
          { path: "start-integration/:id", element: <RedirectStartStep /> },
          { path: "start-integration/:id/source-configuration", element: <RedirectStartStep step="source-configuration" /> },
          { path: "start-integration/:id/destination-configuration", element: <RedirectStartStep step="destination-configuration" /> },
          { path: "start-integration/:id/master-entity", element: <RedirectStartStep step="master-entity" /> },
          {
            path: "start-integration/:id/enrichment-configuration/:enrichmentTitle/:enrichmentId",
            element: <RedirectStartStep step="enrichment-configuration" />,
          },
          { path: "start-integration/:id/data-mapping", element: <RedirectStartStep step="data-mapping" /> },
          { path: "start-integration/:id/sync-schedule", element: <RedirectStartStep step="sync-schedule" /> },
          { path: "start-integration/:id/review", element: <RedirectStartStep step="review" /> },
          { path: "start-integration/:id/integration-profile", element: <RedirectStartStep step="integration-profile" /> },
          { path: "edit-integration/data-mapping/:id", element: <RedirectEditStep step="data-mapping" /> },
          { path: "edit-integration/integration-profile/:id", element: <RedirectEditStep step="integration-profile" /> },
          { path: "edit-integration/source-configuration/:id", element: <RedirectEditStep step="source-configuration" /> },
          { path: "edit-integration/destination-configuration/:id", element: <RedirectEditStep step="destination-configuration" /> },
          {
            path: "edit-integration/enrichment-configuration/:id/:enrichmentId",
            element: <RedirectEditStep step="enrichment-configuration" />,
          },
          { path: "edit-integration/sync-schedule/:id", element: <RedirectEditStep step="sync-schedule" /> },
          { path: "edit-integration/master-entity/:id", element: <RedirectEditStep step="master-entity" /> },
          { path: "edit-integration/:id", element: <RedirectEditStep /> },
          {
            path: "faq",
            element: <FAQ />,
          },
          {
            path: "help-center",
            element: <HelpCenter />,
          },
          {
            path: "settings",
            element: <SettingsPage />,
          },
          {
            path: "account",
            element: <AccountRedirect />,
          },
          {
            path: "settings/billing",
            element: <BillingRedirect />,
          },
          {
            path: "system-settings",
            element: <SystemSettingsRedirect />,
          },
          {
            path: "settings/cache/:cacheId/grid",
            element: <MasterCacheGridPage />,
          },
          {
            path: "system-settings/cache/:cacheId/grid",
            element: <CacheGridRedirect />,
          },
        ],
      },
      {
        path: "*",
        element: <ErrorPage />,
      },
    ]
  }
]; 